"use client";

import { cn } from "@/utils/ui";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { CircleCheckIcon, CircleIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { type ReactNode, useEffect, useState } from "react";

export interface LoadingState {
  text: string;
  icon?: ReactNode;
}

/**
 * A hook that manages loading and completion states when handling promises.
 * Useful for adding delay after a promise resolves before triggering completion actions.
 *
 * @param promise - The promise to track. If undefined, no loading state will be triggered.
 * @param completionDelay - The delay in milliseconds after promise resolution before triggering completion.
 * @param onComplete - Optional callback function to execute after the completion delay.
 * @returns An object containing:
 *   - `isPromiseLoading`: Boolean indicating if the promise is currently loading.
 *   - `isCompleting`: Boolean indicating if the promise has resolved and is in the completion delay period.
 *
 * @example
 * ```tsx
 * const { isPromiseLoading, isCompleting } = usePromiseWithCompletion(
 *   fetchDataPromise,
 *   1000,
 *   () => navigate('/success')
 * );
 * ```
 */
function usePromiseWithCompletion(
  promise: Promise<unknown> | undefined,
  completionDelay: number,
  onComplete?: () => void,
) {
  const [isLoading, setIsLoading] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    if (!promise) {
      return;
    }

    setIsLoading(true);

    const handleCompletion = () => {
      // When promise completes, enter completing state
      setIsCompleting(true);
      setIsLoading(false);

      // After delay, exit completing state and trigger callback
      setTimeout(() => {
        setIsCompleting(false);
        onComplete?.();
      }, completionDelay);
    };

    promise.then(handleCompletion).catch(handleCompletion);

    return () => {
      // Cleanup on unmount
      setIsLoading(false);
      setIsCompleting(false);
    };
  }, [promise, completionDelay, onComplete]);

  return { isPromiseLoading: isLoading, isCompleting };
}

interface LoaderItemProps {
  loadingState: LoadingState;
  index: number;
  currentValue: number;
}

const LoaderItem = ({ loadingState, index, currentValue }: LoaderItemProps) => {
  // Calculate opacity based on distance from current step
  const distance = Math.abs(index - currentValue);
  const opacity = Math.max(1 - distance * 0.2, 0);
  const isActive = index === currentValue;

  return (
    <motion.div
      key={`loader-state-${index}`}
      className="text-left flex gap-2 mb-4"
      initial={{ opacity: 0, y: -(currentValue * 40) }}
      animate={{ opacity, y: -(currentValue * 40) }}
      transition={{ duration: 0.5 }}
    >
      <div>
        {loadingState.icon || (
          <>
            {index > currentValue && (
              <CircleIcon className="text-gray-fg-primary" />
            )}
            {index <= currentValue && (
              <CircleCheckIcon
                className={cn(
                  "text-gray-fg-primary",
                  isActive && "opacity-100",
                )}
              />
            )}
          </>
        )}
      </div>
      <span className={cn("text-gray-fg-primary", isActive && "opacity-100")}>
        {loadingState.text}
      </span>
    </motion.div>
  );
};

interface LoaderCoreProps {
  loadingStates: LoadingState[];
  value?: number;
  className?: string;
}

const LoaderCore = ({
  loadingStates,
  value = 0,
  className,
}: LoaderCoreProps) => {
  return (
    <div
      className={cn(
        "flex relative justify-start max-w-xl mx-auto flex-col mt-40",
        className,
      )}
    >
      {loadingStates.map((loadingState, index) => (
        <LoaderItem
          // biome-ignore lint/suspicious/noArrayIndexKey: This is a static list
          key={index}
          loadingState={loadingState}
          index={index}
          currentValue={value}
        />
      ))}
    </div>
  );
};

export interface MultiStepLoaderProps {
  loadingStates: LoadingState[];
  loading?: boolean;
  duration?: number;
  loop?: boolean;
  backdropClassName?: string;
  loaderClassName?: string;
  onCycleComplete?: () => void;
  promise?: Promise<unknown>;
  completionDelay?: number;
}

/**
 * A component that displays a multi-step loading animation with optional promise integration.
 *
 * The loader can be controlled externally through the loading prop, automatically progress
 * through states based on a provided promise, or both. It supports looping through states
 * or progressing to the final state and stopping.
 *
 * During the completion phase (after a promise resolves), the loader will automatically
 * accelerate through remaining states to provide visual feedback of completion.
 *
 * @param props - The properties for the MultiStepLoader component
 * @param props.loadingStates - Array of loading states to display sequentially
 * @param props.loading - Optional external control for the loading state. Defaults to false
 * @param props.duration - Time in milliseconds between state transitions. Defaults to 1000ms
 * @param props.loop - Whether to loop through states continuously. Defaults to false
 * @param props.backdropClassName - Optional additional CSS classes for the backdrop
 * @param props.loaderClassName - Optional additional CSS classes for the loader container
 * @param props.onCycleComplete - Optional callback called when a full cycle completes in loop mode
 * @param props.promise - Optional promise to track. Loader will show while promise is pending
 * @param props.completionDelay - Time in milliseconds to continue showing the loader after promise resolves. Defaults to 1000ms
 *
 * @returns A React component that displays a multi-step loading animation with backdrop
 */
export const MultiStepLoader = ({
  loadingStates,
  loading: externalLoading = false,
  duration = 1000,
  loop = false,
  backdropClassName,
  loaderClassName,
  onCycleComplete,
  promise,
  completionDelay = 1000,
}: MultiStepLoaderProps) => {
  const [currentState, setCurrentState] = useState(0);
  const { isPromiseLoading, isCompleting } = usePromiseWithCompletion(
    promise,
    completionDelay,
  );

  // Combined loading state from external control or promise
  const isLoading = externalLoading || isPromiseLoading || isCompleting;

  // Handle automatic progression through states during completion phase
  useEffect(() => {
    if (isCompleting && currentState < loadingStates.length - 1) {
      // Use a faster transition during completion
      const transitionTime = Math.min(duration / 2, 500);

      const timeout = setTimeout(() => {
        setCurrentState((prevState) =>
          Math.min(prevState + 1, loadingStates.length - 1),
        );
      }, transitionTime);

      return () => clearTimeout(timeout);
    }
  }, [isCompleting, currentState, loadingStates.length, duration]);

  // Handle normal progression through states during loading
  // biome-ignore lint/correctness/useExhaustiveDependencies: currentState is used to step through states
  useEffect(() => {
    if (isCompleting) {
      return;
    }

    if (!isLoading) {
      // Reset to first state when not loading
      setCurrentState(0);
      return;
    }

    const timeout = setTimeout(() => {
      setCurrentState((prevState) => {
        const isLastState = prevState === loadingStates.length - 1;

        // Call cycle complete callback if we're looping and at the last state
        if (loop && isLastState && onCycleComplete) {
          onCycleComplete();
        }

        // For loop mode: return to first state when reaching the end
        // For non-loop mode: stay at last state when reaching the end
        return loop
          ? isLastState
            ? 0
            : prevState + 1
          : Math.min(prevState + 1, loadingStates.length - 1);
      });
    }, duration);

    return () => clearTimeout(timeout);
  }, [
    currentState,
    isLoading,
    loop,
    loadingStates.length,
    duration,
    onCycleComplete,
    isCompleting,
  ]);

  return (
    <AnimatePresence mode="wait">
      {isLoading && (
        <motion.output
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={cn(
            "size-full fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-2xl",
            backdropClassName,
          )}
          aria-live="polite"
        >
          <div className={cn("h-96 relative", loaderClassName)}>
            <LoaderCore value={currentState} loadingStates={loadingStates} />
          </div>

          {/* Gradient overlay for visual effect */}
          <div className="bg-gradient-to-t inset-x-0 z-20 bottom-0 bg-graya-app-bg h-full absolute [mask-image:radial-gradient(900px_at_center,transparent_30%,white)]" />
        </motion.output>
      )}
    </AnimatePresence>
  );
};

export const MultiStepLoaderModal = NiceModal.create(
  ({ promise, completionDelay = 1000, ...props }: MultiStepLoaderProps) => {
    const modal = useModal();

    // Wrap the original promise to handle modal resolution and hiding
    const enhancedPromise = promise
      ? new Promise((resolve, reject) => {
          promise
            .then((result) => {
              // On promise resolution, resolve the modal with the result after delay
              setTimeout(() => {
                modal.resolve(result);
                modal.hide();
              }, completionDelay);
              resolve(result);
            })
            .catch((error) => {
              // On promise rejection, reject the modal with the error after delay
              setTimeout(() => {
                modal.reject(error);
                modal.hide();
              }, completionDelay);
              reject(error);
            });
        })
      : undefined;

    return (
      modal.visible && (
        <MultiStepLoader
          promise={enhancedPromise}
          completionDelay={completionDelay}
          {...props}
        />
      )
    );
  },
);
